import { useState, type MouseEvent, type ReactNode } from 'react'
import { ChatRuntimeProvider } from '@dev-streetbeat/chat/react'
import { TranslationsProvider } from '@dev-streetbeat/chat-ui'
import { Link, useLocation, useNavigate, useNavigationType } from 'react-router-dom'
import { LogOut } from 'lucide-react'

import type { Thread } from '@dev-streetbeat/chat/types'
import { buildAgentChatConfig } from '../../agent/chatConfig'
import type { AgentConfig } from '../../agent/types'
import { useAceSession } from '../../agent/useAceSession'
import type { AceSession } from '../../agent/useAceSession'
import { DesignerSidebar, SidebarProvider } from '@/components/navigation/DesignerSidebar'
import type {
  DesignerSidebarGroup,
  DesignerSidebarUser,
} from '@/components/navigation/DesignerSidebar'
import { Button } from '@/components/ui/button'
import { CoachmarkProvider } from '@/components/ui/coachmark'
import { TooltipProvider } from '@/components/ui/tooltip'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  TopNavSlotProvider,
  useTopNavSlotRef,
} from '@/components/page-top-nav/PageTopNavOutlet'
import { FeedbackWidget } from '@/components/credem/FeedbackWidget'
import { useAuth } from '../auth/useAuth'
import type { AuthUser } from '../types'
import type { NavItem, RouteKind } from './routeModel'
import { useUnseenCounts } from './useUnseenCounts'
import styles from './AppShell.module.scss'

export function AppShell({
  agentConfig,
  authUser,
  children,
  currentPath,
  currentRoute,
  dashboardThreadId,
  navItems,
  onDashboardThreadChange,
  onDashboardThreadDeleteError,
  onDashboardThreadDeleteStart,
  adminNavItem,
}: {
  agentConfig?: Partial<AgentConfig>
  authUser: AuthUser | null
  children: ReactNode
  currentPath: string
  currentRoute: RouteKind
  dashboardThreadId: string | null
  navItems: NavItem[]
  adminNavItem?: NavItem | null,
  onDashboardThreadChange: (threadId: string | null, thread?: Thread | null) => void
  onDashboardThreadDeleteStart?: (threadId: string) => void
  onDashboardThreadDeleteError?: (threadId: string, error: unknown) => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()
  const aceSessionState = useAceSession()
  const effectiveNavItems = adminNavItem ? [...navItems, adminNavItem] : navItems
  const unseenCounts = useUnseenCounts(currentPath)

  // Sidebar selection moves with the chat context: when the dashboard is
  // sitting in a thread (`?thread_id=…`) or in the focused "Nuova chat"
  // composer state (sidebar push with `state.newChat`), the Dashboard nav
  // row releases its active style so the chat-side selection — a thread
  // row or the "Nuova chat" button — owns the highlight. Initial-load /
  // refresh (`POP`) intentionally reads as a plain dashboard view, mirroring
  // how `HomeRoute` derives `newChatMode`.
  const isNewChatActive =
    currentPath === '/' &&
    navigationType !== 'POP' &&
    Boolean((location.state as { newChat?: boolean } | null)?.newChat)
  const isChatContextActive =
    currentPath === '/' && (isNewChatActive || Boolean(dashboardThreadId))
  const sidebarGroups = groupNavItems(
    effectiveNavItems,
    currentPath,
    unseenCounts,
    { suppressDashboardActive: isChatContextActive },
  )
  const [topNavSlot, setTopNavSlot] = useTopNavSlotRef()

  const sidebar = (
    <DesignerSidebar
      groups={sidebarGroups}
      homeLabel="Vai alla dashboard"
      showGroupHeaders={false}
      user={authUser ? userForSidebar(authUser) : null}
      footerAction={authUser ? <LogoutButton /> : null}
      chatHistory={
        aceSessionState.status === 'ready'
          ? {
              onNewChat: () => {
                onDashboardThreadChange(null, null)
                navigate('/', { state: { newChat: true } })
              },
              onThreadChange: onDashboardThreadChange,
              onThreadDeleteStart: onDashboardThreadDeleteStart,
              onThreadDeleteError: onDashboardThreadDeleteError,
              selectedThreadId: dashboardThreadId,
              isNewChatActive,
            }
          : undefined
      }
    />
  )

  return (
    <SidebarProvider>
      <TooltipProvider delayDuration={150}>
        <CoachmarkProvider>
        <TopNavSlotProvider slot={topNavSlot}>
        <div className={styles.shell} data-bank="credem">
          {aceSessionState.status === 'ready' ? (
            <SidebarChatRuntime
              agentConfig={agentConfig}
              selectedThreadId={dashboardThreadId}
              session={aceSessionState.session}
            >
              {sidebar}
            </SidebarChatRuntime>
          ) : (
            sidebar
          )}

          <main className={styles.main}>
            <div ref={setTopNavSlot} className={styles.topNavSlot} />
            <div className={styles.content}>
              <div className={styles.contentStack}>{children}</div>
            </div>
          </main>

          <FeedbackWidget />
          <Link to="/ds" className={styles.dsLink} aria-label="Design system">ds</Link>
        </div>
        </TopNavSlotProvider>
        </CoachmarkProvider>
      </TooltipProvider>
    </SidebarProvider>
  )
}

function SidebarChatRuntime({
  agentConfig,
  children,
  selectedThreadId,
  session,
}: {
  agentConfig?: Partial<AgentConfig>
  children: ReactNode
  selectedThreadId: string | null
  session: AceSession
}) {
  return (
    <ChatRuntimeProvider
      config={buildAgentChatConfig(session, agentConfig)}
      locale="it"
      threadId={selectedThreadId}
    >
      <TranslationsProvider
        locale="it"
        overrides={{ thinking: 'Sto pensando...', thought: 'Processo' }}
      >
        {children}
      </TranslationsProvider>
    </ChatRuntimeProvider>
  )
}


function LogoutButton() {
  const navigate = useNavigate()
  const { logout } = useAuth()
  const [open, setOpen] = useState(false)
  const [loggingOut, setLoggingOut] = useState(false)

  async function handleLogout(event: MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    if (loggingOut) return
    setLoggingOut(true)
    try {
      await logout()
    } finally {
      navigate('/login', { replace: true })
      setLoggingOut(false)
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <Button
        variant="outline"
        size="sm"
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Esci"
        disabled={loggingOut}
      >
        <LogOut aria-hidden />
        <span>{loggingOut ? 'Uscita...' : 'Esci'}</span>
      </Button>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Uscire da Credem?</AlertDialogTitle>
          <AlertDialogDescription>
            Confermando terminerai la sessione corrente e tornerai alla pagina di accesso.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loggingOut}>Annulla</AlertDialogCancel>
          <AlertDialogAction
            intent="destructive"
            disabled={loggingOut}
            onClick={handleLogout}
          >
            {loggingOut ? 'Uscita...' : 'Esci'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

function groupNavItems(
  items: NavItem[],
  currentPath: string,
  unseenCounts: { todos: number; schedules: number },
  options: { suppressDashboardActive?: boolean } = {},
): DesignerSidebarGroup[] {
  const groups: DesignerSidebarGroup[] = []
  for (const item of items) {
    let group = groups.find((candidate) => candidate.label === item.group)
    if (!group) {
      group = { label: item.group, items: [] }
      groups.push(group)
    }
    const unseenCount =
      item.href === '/todos' ? unseenCounts.todos :
      item.href === '/schedules' ? unseenCounts.schedules :
      0

    // The Dashboard row matches on `/` alone, so a chat-context view of
    // the homepage (thread selected or focused composer) would otherwise
    // keep it highlighted while the chat-side selection is the real one.
    const active =
      item.href === '/' && options.suppressDashboardActive
        ? false
        : item.matches(currentPath)

    group.items.push({
      href: item.href,
      icon: item.icon,
      label: item.label,
      title: item.title,
      active,
      end: item.href === '/',
      hasNotification: unseenCount > 0,
      notificationCount: unseenCount > 0 ? unseenCount : undefined,
    })
  }
  return groups
}

function userForSidebar(user: AuthUser): DesignerSidebarUser {
  const displayName = user.name || user.email
  const initials = displayName
    .split(/[\s.@_-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

  return {
    initials: initials || 'U',
    displayName,
    href: '/profile',
    active: false,
  }
}
